import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { cn } from '../../lib/cn';

export type GaugeSegment = {
  label: string;
  value: number;
  color: string;
  gradientId: string;
  stops: [string, string];
};

type CustomGaugeChartProps = {
  score: number;
  statusLabel: string;
  segments: GaugeSegment[];
  height?: number;
  showStatusInTooltip?: boolean;
  showLegend?: boolean;
  direction?: 'forward' | 'reverse';
};

const NEEDLE_LENGTH = 120;

/* ---- Needle ---- */
const Needle = ({
  cx,
  cy,
  angle,
}: {
  cx: number;
  cy: number;
  angle: number;
}) => (
  <svg
    width="100%"
    height="100%"
    className="absolute inset-0 pointer-events-none"
  >
    <g transform={`rotate(${angle} ${cx} ${cy})`}>
      <path
        d={`
          M ${cx - 7} ${cy}
          L ${cx} ${cy - NEEDLE_LENGTH}
          L ${cx + 7} ${cy}
          Z
        `}
        fill="white"
      />
      <circle cx={cx} cy={cy} r={10} fill="white" />
      <circle cx={cx} cy={cy} r={5} fill="#1E293B" />
    </g>
  </svg>
);

/* ---- Component ---- */
const CustomGaugeChart = ({
  score,
  statusLabel,
  segments,
  height = 320,
  showStatusInTooltip = true,
  showLegend = true,
  direction = 'forward',
}: CustomGaugeChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const { width, height: containerHeight } = size;

  /**
   * User logic for Grace Time Abuse progress:
   * 10 -> +1 (1.1x), 50 -> +10 (1.2x), 100 -> +20 (1.2x)
   * Normalize from total 120 progress to 20 window segment.
   */
  const calculateScaledValue = useCallback((val: number) => {
    const multiplier = val >= 50 ? 1.2 : 1.1;
    return (val * multiplier) / 6;
  }, []);

  // Internal Logic Helpers
  const statusColor = useMemo(() => {
    const found = segments.find(
      (s) => s.label.toLowerCase() === statusLabel.toLowerCase().trim(),
    );
    return found ? found.color : '#CBD5E1';
  }, [segments, statusLabel]);

  const needleAngle = useMemo(() => {
    const statusOrder = segments.map((s) => s.label.toLowerCase());
    const index = statusOrder.indexOf(statusLabel.toLowerCase().trim());
    if (index === -1) return direction === 'reverse' ? 90 : -90;

    const padding = 5;
    const numSegments = segments.length;
    const totalPadding = (numSegments - 1) * padding;
    const segmentWidth = (180 - totalPadding) / numSegments;

    // Get progress within the segment (0-20 range)
    const progress = calculateScaledValue
      ? calculateScaledValue(score)
      : score % 20 || (score === 0 ? 0 : 20);
    const t = progress / 20;
    const clampedT = Math.max(0, Math.min(1, t));

    const startAngle = -90 + index * (segmentWidth + padding);

    if (direction === 'reverse') {
      /**
       * Reverse Logic:
       * 0% (clampedT = 0) is at the Right side of the segment (startAngle + segmentWidth)
       * 100% (clampedT = 1) is at the Left side of the segment (startAngle)
       */
      return startAngle + segmentWidth - clampedT * segmentWidth;
    } else {
      /**
       * Forward Logic (Default):
       * 0% (clampedT = 0) is at the Left side of the segment (startAngle)
       * 100% (clampedT = 1) is at the Right side of the segment (startAngle + segmentWidth)
       */
      return startAngle + clampedT * segmentWidth;
    }
  }, [segments, statusLabel, score, calculateScaledValue, direction]);

  const radius = Math.min(width, containerHeight) / 2;
  const angleInRad = (needleAngle * Math.PI) / 180;

  const tipX = width ? width / 2 + NEEDLE_LENGTH * Math.sin(angleInRad) : 0;
  const tipY = containerHeight
    ? containerHeight * 0.97 - NEEDLE_LENGTH * Math.cos(angleInRad)
    : 0;

  return (
    <>
      <div
        ref={containerRef}
        className="relative w-full z-0"
        style={{ height }}
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
          className="-mt-[160px] relative !z-0"
        >
          <PieChart>
            <defs>
              {segments.map((s) => (
                <radialGradient
                  key={s.gradientId}
                  id={s.gradientId}
                  gradientUnits="userSpaceOnUse"
                  cx="50%"
                  cy="100%"
                  r={radius}
                >
                  <stop offset="0%" stopColor={s.stops[0]} />
                  <stop offset="100%" stopColor={s.stops[1]} />
                </radialGradient>
              ))}
            </defs>
            <Pie
              cx="50%"
              cy="100%"
              data={segments}
              endAngle={0}
              startAngle={180}
              paddingAngle={3}
              // isAnimationActive={false}
              innerRadius={radius * 0.25}
              outerRadius={radius}
              dataKey="value"
            >
              {segments.map((entry, i) => (
                <Cell key={i} fill={`url(#${entry.gradientId})`} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <Needle
          cx={width / 2}
          cy={containerHeight * 0.97}
          angle={needleAngle}
        />

        <div
          className="absolute pointer-events-none flex flex-col items-center"
          style={{
            left: `${tipX}px`,
            top: `${tipY}px`,
            transform: `translate(-50%, -100%) rotate(${needleAngle}deg)`,
            transformOrigin: 'bottom center',
          }}
        >
          <div
            className={cn(
              'text-theme-gray-800 font-bold px-3 py-1.5 rounded-lg whitespace-nowrap shadow-xl relative !text-xs mb-10',
            )}
            style={{ background: statusColor }}
          >
            {showStatusInTooltip && `${statusLabel} `}
            {score?.toFixed(1)}%
            <div
              className="absolute left-1/2 -bottom-1 w-2.5 h-2.5 rotate-45 -translate-x-1/2"
              style={{ background: statusColor }}
            />
          </div>
        </div>
      </div>
      {showLegend && (
        <div className="flex flex-wrap justify-center gap-4 text-sm text-white">
          {segments?.map((d) => (
            <div key={d.label} className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded"
                style={{ background: d.color }}
              />
              {d.label}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default CustomGaugeChart;
